import React, { useState, useEffect } from 'react';
import styles from '../styles/interactive-docs.module.css';
import DocsHeader from './DocsHeader';
import DocsSidebar from './DocsSidebar';
import DocsContent from './DocsContent';
import DocsSearchModal from './DocsSearchModal';
import { ichisSpdRegistry } from '../content/registry';

const InteractiveDocs = ({
    projectName = 'IChis SPD',
    docsConfig = ichisSpdRegistry,
    enabledModules = {
        'nspd-integration': process.env.REACT_APP_MODULE_NSPD !== 'false',
        'printing': process.env.REACT_APP_MODULE_PRINTING !== 'false',
        'cinema-mode': process.env.REACT_APP_MODULE_CINEMA !== 'false',
        'spd-plus': process.env.REACT_APP_MODULE_SPD_PLUS !== 'false'
    },
    onBackToApp
}) => {
    // Находим первый доступный элемент
    const getInitialItemId = () => {
        for (const cat of docsConfig.categories || []) {
            for (const item of cat.items || []) {
                if (!item.requiredModule || enabledModules[item.requiredModule]) {
                    return item.id;
                }
            }
        }
        return 'overview';
    };

    const [activeItemId, setActiveItemId] = useState(getInitialItemId);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Горячая клавиша для поиска (Ctrl+K, Cmd+K, или просто '/')
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Игнорируем если фокус уже в поле ввода (кроме самого поиска)
            const isTypingInInput = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);

            // Ctrl+K / Cmd+K (в любой раскладке)
            if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyK' || e.key.toLowerCase() === 'k' || e.key === 'л')) {
                e.preventDefault();
                e.stopPropagation();
                setIsSearchOpen(prev => !prev);
                return;
            }

            // Быстрый поиск по нажатию клавиши '/' (как в GitHub / Notion)
            if (e.key === '/' && !isTypingInInput && !isSearchOpen) {
                e.preventDefault();
                e.stopPropagation();
                setIsSearchOpen(true);
            }
        };

        // capture: true перехватывает событие до системных обработчиков браузера
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [isSearchOpen]);

    // Поиск активной статьи и категории
    let activeItem = null;
    let activeCategoryTitle = '';

    for (const cat of docsConfig.categories || []) {
        const found = (cat.items || []).find(item => item.id === activeItemId);
        if (found) {
            activeItem = found;
            activeCategoryTitle = cat.title;
            break;
        }
    }

    // Формируем плоский упорядоченный список всех видимых статей с учетом активных модулей
    const flatVisibleItems = [];
    (docsConfig.categories || []).forEach(cat => {
        (cat.items || []).forEach(item => {
            if (!item.requiredModule || enabledModules[item.requiredModule]) {
                flatVisibleItems.push(item);
            }
        });
    });

    const currentIndex = flatVisibleItems.findIndex(item => item.id === activeItemId);
    const prevItem = currentIndex > 0 ? flatVisibleItems[currentIndex - 1] : null;
    const nextItem = (currentIndex >= 0 && currentIndex < flatVisibleItems.length - 1) ? flatVisibleItems[currentIndex + 1] : null;

    return (
        <div className={styles.docsContainer}>
            <DocsHeader
                projectName={projectName}
                onOpenSearch={() => setIsSearchOpen(true)}
                onBackToApp={onBackToApp}
                isMobileMenuOpen={isMobileMenuOpen}
                onToggleMobileMenu={() => setIsMobileMenuOpen(prev => !prev)}
            />

            <div className={styles.docsBody}>
                <DocsSidebar
                    categories={docsConfig.categories}
                    activeItemId={activeItemId}
                    onSelectItem={setActiveItemId}
                    enabledModules={enabledModules}
                    isOpen={isMobileMenuOpen}
                    onClose={() => setIsMobileMenuOpen(false)}
                />

                <DocsContent
                    item={activeItem}
                    categoryTitle={activeCategoryTitle}
                    prevItem={prevItem}
                    nextItem={nextItem}
                    onSelectItem={setActiveItemId}
                />
            </div>

            <DocsSearchModal
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                categories={docsConfig.categories}
                onSelectResult={setActiveItemId}
                enabledModules={enabledModules}
            />
        </div>
    );
};

export default InteractiveDocs;
